import React, { useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import { MdPerson, MdEmail, MdBadge, MdCalendarToday, MdSave, MdImage, MdLogout, MdUpload, MdDelete, MdLock, MdDraw, MdClear } from 'react-icons/md';
import './Settings.css';

const Settings = () => {
  const { user, setUser, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ full_name: '', avatar_url: '', username: '', email: '', phone: '', address: '', date_of_birth: '', sex: '', nationality: '' });
  const [pwData, setPwData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [message, setMessage] = useState('');
  const [pwMessage, setPwMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Signature pad state
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [sigMessage, setSigMessage] = useState({ type: '', text: '' });
  const [sigLoading, setSigLoading] = useState(false);
  const [existingSignature, setExistingSignature] = useState(null);

  useEffect(() => {
    if (user && user.role !== 'admin') { navigate('/profile'); return; }
    if (user) {
      setFormData({ full_name: user.full_name || '', avatar_url: user.avatar_url || '', username: user.username || '', email: user.email || '', phone: user.phone || '', address: user.address || '', date_of_birth: user.date_of_birth ? user.date_of_birth.split('T')[0] : '', sex: user.sex || '', nationality: user.nationality || '' });
      setPhotoPreview(user.avatar_url || null);
      setExistingSignature(user.signature_url || null);
    }
    // Always fetch the shared admin signature
    fetchAdminSignature();
  }, [user, navigate]);

  const fetchAdminSignature = async () => {
    try {
      const res = await axios.get('/api/users/admin-signature');
      if (res.data.signature_url) {
        setExistingSignature(res.data.signature_url);
      }
    } catch (error) {
      console.error('Error fetching admin signature:', error);
    }
  };

  // Signature pad helpers
  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#ffffff';
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    e?.preventDefault();
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = async () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    // Check if canvas is blank
    const blank = document.createElement('canvas');
    blank.width = canvas.width;
    blank.height = canvas.height;
    if (dataUrl === blank.toDataURL('image/png')) {
      setSigMessage({ type: 'error', text: 'Please draw your signature first.' });
      return;
    }
    setSigLoading(true);
    setSigMessage({ type: '', text: '' });
    try {
      const res = await axios.post('/api/users/save-signature', { signature_url: dataUrl });
      setUser({ ...user, signature_url: dataUrl });
      setExistingSignature(dataUrl);
      setSigMessage({ type: 'success', text: 'Signature saved successfully!' });
      setTimeout(() => setSigMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setSigMessage({ type: 'error', text: 'Failed to save signature.' });
    } finally {
      setSigLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const response = await axios.put('/api/users/profile', formData);
      setUser({ ...user, ...response.data });
      setMessage('Profile updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!pwData.current_password || !pwData.new_password || !pwData.confirm_password) {
      setPwMessage({ type: 'error', text: 'All password fields are required.' }); return;
    }
    if (pwData.new_password.length < 6) {
      setPwMessage({ type: 'error', text: 'New password must be at least 6 characters.' }); return;
    }
    if (pwData.new_password !== pwData.confirm_password) {
      setPwMessage({ type: 'error', text: 'New passwords do not match.' }); return;
    }
    setPwLoading(true);
    setPwMessage({ type: '', text: '' });
    try {
      await axios.put('/api/users/profile', { current_password: pwData.current_password, new_password: pwData.new_password });
      setPwMessage({ type: 'success', text: 'Password changed successfully!' });
      setPwData({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => setPwMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setPwMessage({ type: 'error', text: error.response?.data?.error || 'Failed to change password.' });
    } finally {
      setPwLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setMessage('Please select a valid image file.'); return; }
    if (file.size > 5 * 1024 * 1024) { setMessage('Image size must be less than 5MB.'); return; }
    setUploadingPhoto(true);
    try {
      const uploadData = new FormData();
      uploadData.append('photo', file);
      const response = await axios.post('/api/users/upload-photo', uploadData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const newAvatarUrl = response.data.avatar_url;
      setFormData({ ...formData, avatar_url: newAvatarUrl });
      setPhotoPreview(newAvatarUrl);
      setUser({ ...user, avatar_url: newAvatarUrl });
      setMessage('Photo uploaded successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = () => { setFormData({ ...formData, avatar_url: '' }); setPhotoPreview(null); };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return '#EF4444';
      case 'manager': return '#F59E0B';
      default: return '#10B981';
    }
  };

  return (
    <div className="app-container">
      <Sidebar />
      <div className="main-content settings-main">
        <div className="settings-page">
          <div className="settings-header">
            <h1>Settings</h1>
            <p className="settings-subtitle">Manage your account settings and preferences</p>
          </div>

          <div className="settings-grid">
            {/* Left column */}
            <div className="settings-left-col">
              {/* Profile Card */}
              <div className="settings-card profile-card">
                <div className="card-header">
                  <div className="card-icon"><MdPerson /></div>
                  <div>
                    <h2>Profile Settings</h2>
                    <p className="card-description">Update your personal information</p>
                  </div>
                </div>
                <form onSubmit={handleSubmit}>
                  {message && (
                    <div className={`message ${message.includes('Error') || message.includes('failed') || message.includes('taken') ? 'error' : 'success'}`}>
                      {message}
                    </div>
                  )}
                  <div className="form-group">
                    <label><MdPerson className="label-icon" />Username</label>
                    <input type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder="Enter your username" />
                  </div>
                  <div className="form-group">
                    <label><MdEmail className="label-icon" />Email Address</label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Enter your email" />
                  </div>
                  <div className="form-group">
                    <label><MdPerson className="label-icon" />Phone Number</label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="e.g. +251 917 397 648" />
                  </div>
                  <div className="form-group">
                    <label><MdPerson className="label-icon" />Address</label>
                    <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="e.g. Addis Ababa, Ethiopia" />
                  </div>
                  <div className="form-group">
                    <label><MdCalendarToday className="label-icon" />Date of Birth</label>
                    <input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label><MdPerson className="label-icon" />Sex</label>
                    <select value={formData.sex} onChange={(e) => setFormData({ ...formData, sex: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '2px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', color: '#1F2937', background: 'white' }}>
                      <option value="">— Select —</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label><MdPerson className="label-icon" />Nationality</label>
                    <input type="text" value={formData.nationality} onChange={(e) => setFormData({ ...formData, nationality: e.target.value })} placeholder="e.g. Ethiopian" />
                  </div>
                  <div className="form-group">
                    <label><MdPerson className="label-icon" />Full Name</label>
                    <input type="text" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} placeholder="Enter your full name" />
                  </div>
                  <div className="form-group">
                    <label><MdImage className="label-icon" />Profile Photo</label>
                    {photoPreview && (
                      <div className="photo-preview-container">
                        <img src={photoPreview} alt="Profile preview" className="photo-preview-large" onError={(e) => e.target.style.display = 'none'} />
                        <button type="button" className="btn-remove-photo" onClick={handleRemovePhoto}><MdDelete /></button>
                      </div>
                    )}
                    <div className="photo-upload-section">
                      <input type="file" id="photo-upload" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} disabled={uploadingPhoto} />
                      <label htmlFor="photo-upload" className={`btn-upload-photo ${uploadingPhoto ? 'uploading' : ''}`}>
                        <MdUpload />{uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                      </label>
                      <small><MdImage className="small-icon" /> Max size: 5MB. JPG, PNG, GIF</small>
                    </div>
                  </div>
                  <div className="form-group">
                    <label><MdImage className="label-icon" />Or Enter Avatar URL</label>
                    <input type="url" value={formData.avatar_url} onChange={(e) => { setFormData({ ...formData, avatar_url: e.target.value }); setPhotoPreview(e.target.value); }} placeholder="https://example.com/avatar.jpg" />
                  </div>
                  <button type="submit" className="btn-save" disabled={loading}>
                    <MdSave />{loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>
              </div>

              {/* Change Password Card */}
              <div className="settings-card">
                <div className="card-header">
                  <div className="card-icon" style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}><MdLock /></div>
                  <div>
                    <h2>Change Password</h2>
                    <p className="card-description">Update your account password</p>
                  </div>
                </div>
                <form onSubmit={handlePasswordSubmit}>
                  {pwMessage.text && <div className={`message ${pwMessage.type}`}>{pwMessage.text}</div>}
                  <div className="form-group">
                    <label>Current Password</label>
                    <input type="password" value={pwData.current_password} onChange={(e) => setPwData({ ...pwData, current_password: e.target.value })} placeholder="Enter current password" />
                  </div>
                  <div className="form-group">
                    <label>New Password</label>
                    <input type="password" value={pwData.new_password} onChange={(e) => setPwData({ ...pwData, new_password: e.target.value })} placeholder="Min 6 characters" />
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input type="password" value={pwData.confirm_password} onChange={(e) => setPwData({ ...pwData, confirm_password: e.target.value })} placeholder="Confirm new password" />
                  </div>
                  <button type="submit" className="btn-save" disabled={pwLoading}>
                    <MdLock />{pwLoading ? 'Updating...' : 'Update Password'}
                  </button>
                </form>
              </div>

              {/* Signature Card */}
              <div className="settings-card">
                <div className="card-header">
                  <div className="card-icon" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)' }}><MdDraw /></div>
                  <div>
                    <h2>Authorized Signature</h2>
                    <p className="card-description">Draw your signature for ID cards</p>
                  </div>
                </div>

                {sigMessage.text && <div className={`message ${sigMessage.type}`}>{sigMessage.text}</div>}

                {existingSignature && (
                  <div className="signature-preview">
                    <p className="sig-preview-label">Current Authorized Signature (used on all ID cards):</p>
                    <div className="sig-preview-box">
                      <img src={existingSignature} alt="Current signature" />
                    </div>
                  </div>
                )}

                <p className="sig-instruction">Draw your signature below:</p>
                <div className="signature-canvas-wrapper">
                  <canvas
                    ref={canvasRef}
                    width={500}
                    height={150}
                    className="signature-canvas"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                </div>
                <div className="sig-actions">
                  <button type="button" className="btn-secondary sig-clear-btn" onClick={clearSignature}>
                    <MdClear /> Clear
                  </button>
                  <button type="button" className="btn-save" onClick={saveSignature} disabled={sigLoading}>
                    <MdSave />{sigLoading ? 'Saving...' : 'Save Signature'}
                  </button>
                </div>
              </div>
            </div>

            {/* Right sidebar */}
            <div className="settings-sidebar">
              <div className="settings-card account-card">
                <div className="card-header">
                  <div className="card-icon"><MdBadge /></div>
                  <div>
                    <h2>Account Information</h2>
                    <p className="card-description">Your account details</p>
                  </div>
                </div>
                <div className="info-list">
                  <div className="info-item">
                    <div className="info-icon" style={{ background: `${getRoleColor(user?.role)}15`, border: `2px solid ${getRoleColor(user?.role)}30` }}>
                      <MdBadge style={{ color: getRoleColor(user?.role) }} />
                    </div>
                    <div className="info-content">
                      <span className="info-label">Role</span>
                      <span className="info-value">{user?.role || 'user'}</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <div className="info-icon" style={{ background: '#DBEAFE', border: '2px solid #3B82F6' }}>
                      <MdCalendarToday style={{ color: '#1E40AF' }} />
                    </div>
                    <div className="info-content">
                      <span className="info-label">Member Since</span>
                      <span className="info-value">
                        {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="settings-card stats-card">
                <h3>Quick Stats</h3>
                <div className="stats-list">
                  <div className="stat-item">
                    <span className="stat-label">Account Status</span>
                    <span className="stat-badge active">Active</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Profile Completion</span>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${(formData.full_name ? 50 : 0) + (formData.avatar_url ? 50 : 0)}%` }}></div>
                    </div>
                    <span className="stat-value">{(formData.full_name ? 50 : 0) + (formData.avatar_url ? 50 : 0)}%</span>
                  </div>
                </div>
              </div>

              <div className="settings-card logout-card">
                <button onClick={() => { logout(); navigate('/login'); }} className="btn-logout-mobile">
                  <MdLogout />Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
